import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CapsuleGeometry,
  Color,
  CylinderGeometry,
  LatheGeometry,
  MeshStandardMaterial,
  Quaternion,
  ShaderMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
  type Group,
  type Sprite,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Building } from '@map/shared';
import { strengthGlow } from '@map/health';
import { containsPoint, type Territory } from '@map/worldkit';
import { isTouch } from '../lib/useIsMobile.js';
import { prefersReducedMotion, sceneColor } from '../styles/tokens.js';
import { frameOf, type Frame } from './ArabicLand.js';
import { buildTerritoryGeometry } from './geometry.js';
import { glowTexture } from './planetSkin.js';

/**
 * Strength of self (Growth › Values): a figure seated in meditation in the
 * middle of its country.
 *
 * Gold rises through it from the cushion up, a day at a time: at day 20 it is
 * gold to the chest, and from day 40 it is gold to the crown. Its aura grows
 * with it, and once it is wholly gold the light spills out over the whole
 * country — the ground glows, rays turn slowly about it, motes rise. A reset
 * takes it back to stone. Clicking it opens its calendar.
 */
export interface MeditatorProps {
  territories: Map<string, Territory>;
  buildings: Building[];
  onOpen?: (regionId: string, buildingId: string) => void;
}

/** Land is drawn at 1.02; the figure's cushion sits a hair above it. */
const GROUND = 1.0205;
/** The country's light lies just above the soil, under everything standing. */
const LIGHT = 0.0206;
/** The figure is built one unit tall; this is where the gold ends at full. */
const CROWN = 0.98;
/** How far back the figure may lean toward a camera overhead, in radians. */
const MAX_LEAN = 0.95;

interface Seat {
  id: string;
  regionId: string;
  frame: Frame;
  /** Where it sits: a unit vector on the land. */
  at: Vector3;
  /** 0 = stone, 1 = wholly gold (day 40 on). */
  glow: number;
  height: number;
}

export function Meditator({ territories, buildings, onOpen }: MeditatorProps) {
  const seats = useMemo(() => {
    const out: Seat[] = [];
    for (const b of buildings) {
      if (b.kind !== 'strength' || !b.strength) continue;
      const territory = territories.get(b.regionId);
      if (!territory) continue;
      const frame = frameOf(territory);
      // A little south of the middle, so the place's names sit above its head.
      const south = frame.up
        .clone()
        .addScaledVector(frame.north, -frame.reach * 0.22)
        .normalize();
      const at = containsPoint(territory.ring, { x: south.x, y: south.y, z: south.z })
        ? south
        : frame.up.clone();
      out.push({
        id: b.id,
        regionId: b.regionId,
        frame,
        at,
        glow: strengthGlow(b.strength.count),
        height: Math.min(0.1, Math.max(0.03, frame.reach * 0.3)),
      });
    }
    return out;
  }, [buildings, territories]);

  const root = useRef<Group>(null);
  usePicking(seats, root, onOpen);

  if (seats.length === 0) return null;
  return (
    <group ref={root}>
      {seats.map((seat) => (
        <Figure key={seat.id} seat={seat} root={root} />
      ))}
    </group>
  );
}

/** Body parts in the figure's own space: one unit tall, seated at y = 0, facing +z. */
function bodyGeometry(): BufferGeometry {
  const Y = new Vector3(0, 1, 0);
  const limb = (from: Vector3, to: Vector3, radius: number) => {
    const dir = to.clone().sub(from);
    const g = new CapsuleGeometry(radius, Math.max(0.001, dir.length()), 8, 18);
    g.applyQuaternion(new Quaternion().setFromUnitVectors(Y, dir.clone().normalize()));
    const mid = from.clone().add(to).multiplyScalar(0.5);
    return g.translate(mid.x, mid.y, mid.z);
  };
  const ball = (at: Vector3, r: number, sx = 1, sy = 1, sz = 1) =>
    new SphereGeometry(r, 20, 14).scale(sx, sy, sz).translate(at.x, at.y, at.z);

  // The torso, turned on a lathe: waist, chest, shoulders closing to the neck.
  const torso = new LatheGeometry(
    [
      [0.0, 0.0],
      [0.19, 0.0],
      [0.18, 0.1],
      [0.2, 0.24],
      [0.215, 0.33],
      [0.17, 0.4],
      [0.07, 0.44],
      [0.0, 0.445],
    ].map(([r, y]) => new Vector2(r, y)),
    28,
  )
    .scale(1, 1, 0.72)
    .translate(0, 0.2, 0);

  const shoulderL = new Vector3(-0.19, 0.58, 0);
  const shoulderR = new Vector3(0.19, 0.58, 0);
  const elbowL = new Vector3(-0.27, 0.36, 0.05);
  const elbowR = new Vector3(0.27, 0.36, 0.05);
  const handL = new Vector3(-0.21, 0.2, 0.2);
  const handR = new Vector3(0.21, 0.2, 0.2);

  const parts = [
    // Crossed legs: each shin runs across in front to the other knee.
    limb(new Vector3(-0.3, 0.14, 0.02), new Vector3(0.2, 0.13, 0.24), 0.085),
    limb(new Vector3(0.3, 0.16, 0.02), new Vector3(-0.2, 0.15, 0.24), 0.085),
    ball(new Vector3(-0.3, 0.15, 0.02), 0.09),
    ball(new Vector3(0.3, 0.15, 0.02), 0.09),
    ball(new Vector3(0.22, 0.12, 0.25), 0.05, 1.2, 0.7, 1),
    ball(new Vector3(-0.22, 0.14, 0.25), 0.05, 1.2, 0.7, 1),
    // Hips, the body, the neck and head with its knot of hair.
    ball(new Vector3(0, 0.2, 0), 0.24, 1, 0.55, 0.78),
    torso,
    new CylinderGeometry(0.05, 0.055, 0.1, 14).translate(0, 0.68, 0),
    ball(new Vector3(0, 0.8, 0.005), 0.11, 0.92, 1.05, 0.95),
    ball(new Vector3(0, 0.925, -0.01), 0.045),
    // Arms down to the hands resting on the knees.
    ball(shoulderL, 0.065),
    ball(shoulderR, 0.065),
    limb(shoulderL, elbowL, 0.05),
    limb(shoulderR, elbowR, 0.05),
    limb(elbowL, handL, 0.045),
    limb(elbowR, handR, 0.045),
    ball(handL, 0.045, 1.1, 0.8, 1.2),
    ball(handR, 0.045, 1.1, 0.8, 1.2),
  ].map((g) => g.toNonIndexed());
  // mergeGeometries wants the same attributes everywhere; lathe and capsule both carry uv.
  const merged = mergeGeometries(parts);
  for (const p of parts) p.dispose();
  return merged;
}

/**
 * Stone below the gold line and gold above it, with the line itself lit. The
 * line is a height in the figure's own space, carried in `uFill`.
 */
function bodyMaterial(stone: string, gold: string, light: string) {
  const fill = { value: 0 };
  const time = { value: 0 };
  const material = new MeshStandardMaterial({
    color: new Color(stone),
    roughness: 0.8,
    metalness: 0.05,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFill = fill;
    shader.uniforms.uTime = time;
    shader.uniforms.uGold = { value: new Color(gold) };
    shader.uniforms.uLight = { value: new Color(light) };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying float vFigY;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFigY = position.y;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uFill;
uniform float uTime;
uniform vec3 uGold;
uniform vec3 uLight;
varying float vFigY;
float goldness() { return 1.0 - smoothstep(uFill - 0.012, uFill + 0.012, vFigY); }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float g = goldness();
diffuseColor.rgb = mix(diffuseColor.rgb, uGold, g);`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
roughnessFactor = mix(roughnessFactor, 0.28, g);`,
      )
      .replace(
        '#include <metalnessmap_fragment>',
        `#include <metalnessmap_fragment>
metalnessFactor = mix(metalnessFactor, 0.45, g);`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
// Gold carries a little of its own light; the rising edge carries more.
float edge = (1.0 - smoothstep(0.0, 0.045, abs(vFigY - uFill))) * step(uFill, ${CROWN.toFixed(2)} - 0.001);
float shimmer = 0.85 + 0.15 * sin(uTime * 2.2 + vFigY * 30.0);
totalEmissiveRadiance += uGold * g * 0.32 * shimmer + uLight * edge * 0.9;`,
      );
  };
  return { material, fill, time };
}

/**
 * Light lying over the whole country, brightest at the figure and fading to
 * the shore. Each vertex carries where it is around the figure (flat
 * coordinates, a unit = the country's reach), so the rays can be drawn per
 * pixel and the coastline cuts them off.
 */
function countryLight(frame: Frame, at: Vector3): BufferGeometry | null {
  const g = buildTerritoryGeometry(frame.territory.ring, { elevation: LIGHT });
  if (!g) return null;
  const pos = g.getAttribute('position');
  const flat = new Float32Array(pos.count * 2);
  const east = new Vector3()
    .crossVectors(Math.abs(at.y) > 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0), at)
    .normalize();
  const north = new Vector3().crossVectors(at, east);
  const unit = Math.max(1e-4, Math.sin(frame.reach));
  const v = new Vector3();
  for (let i = 0; i < pos.count; i += 1) {
    v.fromBufferAttribute(pos, i).normalize();
    v.addScaledVector(at, -v.dot(at));
    flat.set([v.dot(east) / unit, v.dot(north) / unit], i * 2);
  }
  g.setAttribute('aFlat', new BufferAttribute(flat, 2));
  return g;
}

function lightMaterial(colour: Color) {
  const uniforms = {
    uColour: { value: colour },
    uGlow: { value: 0 },
    uRays: { value: 0 },
    uTime: { value: 0 },
  };
  const material = new ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    vertexShader: `
attribute vec2 aFlat;
varying vec2 vFlat;
void main() {
  vFlat = aFlat;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`,
    fragmentShader: `
uniform vec3 uColour;
uniform float uGlow;
uniform float uRays;
uniform float uTime;
varying vec2 vFlat;
void main() {
  float d = clamp(length(vFlat), 0.0, 1.0);
  float a = atan(vFlat.y, vFlat.x);
  float fall = 0.3 + 0.7 * pow(1.0 - d, 1.6);
  // Two sets of rays turning against each other, long and short.
  float outer = pow(0.5 + 0.5 * cos(12.0 * (a - uTime * 0.04)), 10.0);
  float inner = pow(0.5 + 0.5 * cos(12.0 * (a + uTime * 0.03) + 3.14159), 14.0) * (1.0 - smoothstep(0.2, 0.75, d));
  float rays = (outer + 0.7 * inner) * smoothstep(0.04, 0.14, d) * (1.0 - 0.55 * d);
  float k = uGlow * fall + uRays * 0.42 * rays;
  gl_FragColor = vec4(uColour * k, 1.0);
}`,
  });
  return { material, uniforms };
}

const MOTES = 28;

function Figure({ seat, root }: { seat: Seat; root: React.RefObject<Group | null> }) {
  const { frame, glow, height } = seat;
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const still = prefersReducedMotion();
  const whole = glow >= 1;

  const kit = useMemo(() => {
    const gold = sceneColor('strength-gold', '#f3c44a');
    const body = bodyMaterial(
      sceneColor('strength-stone', '#868c99'),
      gold,
      sceneColor('strength-gold-light', '#fff3c4'),
    );
    const halo = new Color(gold);
    return {
      body,
      geometry: bodyGeometry(),
      cushion: new CylinderGeometry(0.44, 0.47, 0.09, 32).translate(0, 0.045, 0),
      cushionMaterial: new MeshStandardMaterial({
        color: new Color(sceneColor('ribbon-4', '#7a3d73')),
        roughness: 0.9,
      }),
      glow: glowTexture(),
      halo,
      light: countryLight(frame, seat.at),
      shine: lightMaterial(halo),
      motes: Array.from({ length: MOTES }, (_, i) => ({
        angle: (i * 2.399963) % (Math.PI * 2),
        radius: 0.25 + ((i * 0.618) % 1) * 1.1,
        speed: 0.12 + ((i * 0.37) % 1) * 0.18,
        phase: (i * 0.731) % 1,
      })),
    };
  }, [frame, seat.at]);
  useEffect(
    () => () => {
      kit.geometry.dispose();
      kit.cushion.dispose();
      kit.body.material.dispose();
      kit.cushionMaterial.dispose();
      kit.glow?.dispose();
      kit.light?.dispose();
      kit.shine.material.dispose();
    },
    [kit],
  );

  const { position, quaternion, inverse } = useMemo(() => {
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), seat.at);
    return {
      position: seat.at.clone().multiplyScalar(GROUND),
      quaternion: q,
      inverse: q.clone().invert(),
    };
  }, [seat.at]);

  const turn = useRef<Group>(null);
  const lean = useRef<Group>(null);
  const halo = useRef<Sprite>(null);
  const aura = useRef<Sprite>(null);
  const motes = useRef<Group>(null);
  const local = useMemo(() => new Vector3(), []);
  const toCamera = useMemo(() => new Vector3(), []);
  const rootTurn = useMemo(() => new Quaternion(), []);
  // The gold climbs to its line when the figure first appears, rather than snapping.
  const shown = useRef(0);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    // Turn to face the camera about its own upright.
    if (turn.current) {
      // Face down the screen, so the head is always toward the top of it:
      // the camera's own up, brought into the figure's frame and laid flat.
      local.set(0, 1, 0).applyQuaternion(camera.quaternion);
      if (root.current) local.applyQuaternion(root.current.getWorldQuaternion(rootTurn).invert());
      local.applyQuaternion(inverse);
      turn.current.rotation.y = Math.atan2(-local.x, -local.z);
      // Seen from high above, a seated figure is all cushion and crown: lean it
      // back toward the camera, short of lying down, so it reads from orbit.
      if (lean.current) {
        toCamera.copy(camera.position);
        if (root.current) root.current.worldToLocal(toCamera);
        toCamera.sub(position).applyQuaternion(inverse);
        const elevation = Math.atan2(toCamera.y, Math.hypot(toCamera.x, toCamera.z));
        lean.current.rotation.x = -Math.min(MAX_LEAN, Math.max(0, elevation - 0.35));
      }
    }
    const target = glow;
    const settling = Math.abs(shown.current - target) > 0.001;
    shown.current = still
      ? target
      : shown.current + (target - shown.current) * Math.min(1, delta * 1.5);
    const p = shown.current;
    kit.body.fill.value = p * CROWN;
    kit.body.time.value = t;

    const breath = still ? 0.5 : 0.5 + 0.5 * Math.sin(t * 0.9);
    if (halo.current) {
      halo.current.scale.setScalar(height * (0.35 + 0.55 * p) * (0.95 + 0.08 * breath));
      (halo.current.material as { opacity: number }).opacity = 0.12 + 0.75 * p;
    }
    if (aura.current) {
      aura.current.scale.setScalar(height * (0.8 + 2.4 * p * p) * (0.94 + 0.1 * breath));
      (aura.current.material as { opacity: number }).opacity = 0.05 + 0.5 * p * p;
    }
    // The country lights a little as the gold climbs; once it is whole the
    // light fills the land and the rays come out.
    kit.shine.uniforms.uGlow.value = whole ? 0.22 + 0.1 * breath : 0.1 * p * p;
    kit.shine.uniforms.uRays.value = whole ? 0.85 + 0.15 * breath : 0;
    kit.shine.uniforms.uTime.value = still ? 0 : t;
    if (motes.current) {
      motes.current.children.forEach((child, i) => {
        const m = kit.motes[i];
        if (!m) return;
        const life = still ? m.phase : (t * m.speed + m.phase) % 1;
        child.position.set(Math.cos(m.angle) * m.radius, life * 1.6, Math.sin(m.angle) * m.radius);
        child.scale.setScalar(0.07 * Math.sin(life * Math.PI));
      });
    }
    if (!still || settling) invalidate();
  });

  return (
    <>
      {kit.light && <mesh geometry={kit.light} material={kit.shine.material} renderOrder={2} />}
      <group position={position} quaternion={quaternion}>
        <group scale={height}>
          <group ref={turn}>
            <group ref={lean}>
              <mesh geometry={kit.cushion} material={kit.cushionMaterial} />
              <mesh geometry={kit.geometry} material={kit.body.material} position={[0, 0.08, 0]} />
              {kit.glow && (
                <>
                  <sprite ref={aura} position={[0, 0.5, -0.1]}>
                    <spriteMaterial
                      map={kit.glow}
                      color={kit.halo}
                      blending={AdditiveBlending}
                      depthWrite={false}
                      transparent
                      toneMapped={false}
                    />
                  </sprite>
                  <sprite ref={halo} position={[0, 0.88, -0.12]}>
                    <spriteMaterial
                      map={kit.glow}
                      color={kit.halo}
                      blending={AdditiveBlending}
                      depthWrite={false}
                      transparent
                      toneMapped={false}
                    />
                  </sprite>
                </>
              )}
              {whole && kit.glow && (
                <group ref={motes}>
                  {kit.motes.map((_, i) => (
                    <sprite key={i}>
                      <spriteMaterial
                        map={kit.glow}
                        color={kit.halo}
                        blending={AdditiveBlending}
                        depthWrite={false}
                        transparent
                        toneMapped={false}
                      />
                    </sprite>
                  ))}
                </group>
              )}
            </group>
          </group>
        </group>
      </group>
    </>
  );
}

/** A click (not a drag) near the figure opens its calendar. */
function usePicking(
  seats: Seat[],
  root: React.RefObject<Group | null>,
  onOpen: MeditatorProps['onOpen'],
) {
  const { camera, gl } = useThree();
  useEffect(() => {
    if (!onOpen || seats.length === 0) return;
    const canvas = gl.domElement;
    const screen = new Vector3();
    const edge = new Vector3();
    const world = new Vector3();
    const toCamera = new Vector3();
    const pick = (event: PointerEvent): Seat | null => {
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      for (const seat of seats) {
        const local = seat.at.clone().multiplyScalar(GROUND + seat.height * 0.5);
        const at = root.current ? world.copy(local).applyMatrix4(root.current.matrixWorld) : local;
        toCamera.copy(camera.position).sub(at);
        if (toCamera.dot(at) <= 0) continue;
        screen.copy(at).project(camera);
        edge
          .copy(at)
          .addScaledVector(at, (seat.height * 0.6) / at.length())
          .project(camera);
        const sx = ((screen.x + 1) / 2) * rect.width;
        const sy = ((1 - screen.y) / 2) * rect.height;
        const ex = ((edge.x + 1) / 2) * rect.width;
        const ey = ((1 - edge.y) / 2) * rect.height;
        const reach =
          Math.min(90, Math.max(18, Math.hypot(ex - sx, ey - sy) * 1.4)) * (isTouch() ? 1.6 : 1);
        if (Math.hypot(sx - px, sy - py) < reach) return seat;
      }
      return null;
    };
    let from: { x: number; y: number; id: number } | null = null;
    const onDown = (e: PointerEvent) => (from = { x: e.clientX, y: e.clientY, id: e.pointerId });
    const onUp = (e: PointerEvent) => {
      const began = from;
      from = null;
      if (
        !began ||
        began.id !== e.pointerId ||
        Math.hypot(e.clientX - began.x, e.clientY - began.y) > 8
      )
        return;
      const seat = pick(e);
      if (seat) onOpen(seat.regionId, seat.id);
    };
    const onCancel = () => (from = null);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
    };
  }, [seats, root, camera, gl, onOpen]);
}
